import { axiosAuthInstance } from '../../../../configs/AxiosInstance';
import { authHeaders, enc, encCode, getBlob, getJson, postJson, readDisposition, rethrowBlobError } from './http';

export { rethrowBlobError, toQuery } from './http';

/**
 * One function per staff endpoint, S1 to S20 (Phase 2 section 0).
 *
 * - Everything goes through `axiosAuthInstance` (`…/auth/welldhan`).
 * - Every GET takes `{ signal }` (an AbortSignal) so a changed range or an
 *   unmounted panel cancels in-flight work (U73).
 * - JSON calls resolve to the envelope's `data`; blob calls resolve to
 *   `{ blob, filename }`, where `filename` is null when `Content-Disposition`
 *   is unreadable (U77) and the caller falls back to `downloadNames.js`.
 * - Errors are thrown as-is (axios errors); read them with `linkErrors.js`.
 */

/** S4 client timeout, longer than the server's 20 s deadline (A08). */
export const MODIFY_TIMEOUT_MS = 30000;

/* ── Links and campaigns ─────────────────────────────────────────────── */

/** S1 `GET /links/meta` */
export const getLinksMeta = (opts) => getJson('/links/meta', undefined, opts);

/** S2 `GET /links` */
export const listLinks = (params, opts) => getJson('/links', params, opts);

/** S3 `GET /links/{code}` */
export const getLink = (code, opts) => getJson(`/links/${encCode(code)}`, undefined, opts);

/**
 * S4 `POST /modify-links`. `idempotencyKey` is minted by `useModifyLink`.
 * @param {{ links: Array<Object>, event_type_id?: string }} body
 * @param {{ idempotencyKey?: string, signal?: AbortSignal, timeout?: number }} [opts]
 */
export const modifyLinks = (body, { idempotencyKey, signal, timeout = MODIFY_TIMEOUT_MS } = {}) =>
  postJson('/modify-links', body, {
    signal,
    timeout,
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
  });

/** S5 `GET /links/{code}/qr` → `{ blob, filename }` */
export const getLinkQr = (code, params, opts) => getBlob(`/links/${encCode(code)}/qr`, params, opts);

/**
 * S6 `POST /links/print-sheet` → `{ blob, filename }`
 * @param {{ codes: string[], template_key: string, ecc: string }} body
 */
export async function printSheet(body, { signal } = {}) {
  const response = await axiosAuthInstance.post('/links/print-sheet', body, {
    signal,
    responseType: 'blob',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  }).catch(rethrowBlobError);
  return { blob: response.data, filename: readDisposition(response.headers) };
}

/** S7 `GET /links/{code}/changes` */
export const getLinkChanges = (code, params, opts) =>
  getJson(`/links/${encCode(code)}/changes`, params, opts);

/** S8 `GET /link-campaigns` */
export const listCampaigns = (params, opts) => getJson('/link-campaigns', params, opts);

/** S9 `GET /link-campaigns/{id}` */
export const getCampaign = (id, opts) => getJson(`/link-campaigns/${enc(id)}`, undefined, opts);

/** S10 `POST /modify-link-campaigns` */
export const modifyCampaigns = (body, opts) => postJson('/modify-link-campaigns', body, opts);

/** S11 `POST /link-campaigns/{id}/disable-all-links` (always confirms, A52). */
export const disableAllCampaignLinks = (id, opts) =>
  postJson(`/link-campaigns/${enc(id)}/disable-all-links`, { confirm: true }, opts);

/* ── Analytics (each takes from, to, include_all) ───────────────────── */

/** S12 `GET /links/{code}/timeseries` */
export const getLinkTimeseries = (code, params, opts) =>
  getJson(`/links/${encCode(code)}/timeseries`, params, opts);

/** S13 `GET /links/{code}/breakdowns` */
export const getLinkBreakdowns = (code, params, opts) =>
  getJson(`/links/${encCode(code)}/breakdowns`, params, opts);

/** S14 `GET /links/{code}/map` */
export const getLinkMap = (code, params, opts) => getJson(`/links/${encCode(code)}/map`, params, opts);

/**
 * S15 `GET /links/{code}/hits`. Pass params through `ipParams.stripIpParams`
 * first; IP parameters must never reach the server for non-Executives (U11).
 */
export const getLinkHits = (code, params, opts) => getJson(`/links/${encCode(code)}/hits`, params, opts);

/** S16 `GET /link-campaigns/{id}/analytics` */
export const getCampaignAnalytics = (id, params, opts) =>
  getJson(`/link-campaigns/${enc(id)}/analytics`, params, opts);

/** S17 `GET /link-analytics/overview` */
export const getOverview = (params, opts) => getJson('/link-analytics/overview', params, opts);

/** S18 `GET /link-analytics/regions` */
export const getRegions = (params, opts) => getJson('/link-analytics/regions', params, opts);

/* ── Exports (Executive only) ───────────────────────────────────────── */

/** S19 `GET /link-exports/hits.csv` → `{ blob, filename }`. Strip IP params first (U11). */
export const exportHitsCsv = (params, opts) => getBlob('/link-exports/hits.csv', params, opts);

/** S20 `GET /link-exports/campaign-summary.csv` → `{ blob, filename }` */
export const exportCampaignSummaryCsv = (params, opts) =>
  getBlob('/link-exports/campaign-summary.csv', params, opts);
